import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Button, Col, Empty, Row, Typography } from 'antd';
import Chart from 'react-apexcharts';
import type { ApexOptions } from 'apexcharts';

import { ASSET_CLASSES, PlayerAnalysisType, selectors } from 'state';
import './analysisStyles.css';

// Your per-instrument tab is already Apex:
import PerInstrumentPnLDistributionTab from './PerInstrumentPnLEvolutionTabApex';

const { Text } = Typography;

export type Props = {
  player: PlayerAnalysisType;
};

const CHART_HEIGHT_PX = 420;

function formatMillions(v: number) {
  const sign = v < 0 ? '-' : '';
  const abs = Math.abs(v);

  if (abs >= 1_000_000_000)
    return `${sign}${(abs / 1_000_000_000).toFixed(0)}B`;
  if (abs >= 1_000_000) return `${sign}${(abs / 1_000_000).toFixed(0)}M`;
  if (abs >= 1_000) return `${sign}${(abs / 1_000).toFixed(0)}K`;
  return `${sign}${abs.toFixed(0)}`;
}

function truncateLabel(s: string, max = 18) {
  if (s.length <= max) return s;
  return `${s.slice(0, max - 3)}...`;
}

const PlayerPnLDistributionTab: React.FC<Props> = ({ player }) => {
  const session = useSelector(selectors.activeSession);
  const repCcy = useSelector(
    selectors.currencyById(session.reporting_currency)
  );
  const [activeTab, setActiveTab] = useState<string>('100'); // 100 = overall

  const pnlDistribution = useSelector(
    selectors.pnlDistributionByPlayerId(player.player_id)
  );

  const { categories, values, totalPnl, minVal, maxVal } = useMemo(() => {
    if (!pnlDistribution) {
      return {
        categories: [] as string[],
        values: [] as number[],
        totalPnl: 0,
        minVal: 0,
        maxVal: 0,
      };
    }

    const items = pnlDistribution.distributions.map((d: any) => {
      const name =
        ASSET_CLASSES[d.asset_class] ?? `Asset class ${d.asset_class}`;
      return { name, y: Math.round(d.reporting_pnl_value ?? 0) };
    });

    const cats = items.map(i => i.name);
    const vals = items.map(i => i.y);
    const total = vals.reduce((acc, v) => acc + v, 0);

    return {
      categories: cats,
      values: vals,
      totalPnl: total,
      minVal: vals.length ? Math.min(...vals) : 0,
      maxVal: vals.length ? Math.max(...vals) : 0,
    };
  }, [pnlDistribution]);

  const hasData = (pnlDistribution?.distributions?.length ?? 0) > 0;

  const playableAssetClasses_ = useMemo(() => {
    if (!pnlDistribution) return [100];

    const arr = pnlDistribution.distributions.map(
      (item: any) => item.asset_class
    );
    arr.push(100);
    arr.reverse();
    return arr;
  }, [pnlDistribution]);

  const formattedTotal = useMemo(() => {
    return `${Math.round(totalPnl).toLocaleString('en-US', {
      maximumFractionDigits: 0,
    })} ${repCcy.symbol}`;
  }, [totalPnl, repCcy.symbol]);

  const series = useMemo(() => [{ name: ' ', data: values }], [values]);

  const options = useMemo((): ApexOptions => {
    const span = Math.max(Math.abs(minVal), Math.abs(maxVal));
    const pad = Math.max(span * 0.1, 1);

    const xMin = Math.min(0, minVal) - pad;
    const xMax = Math.max(0, maxVal) + pad;

    return {
      chart: {
        type: 'bar',
        height: CHART_HEIGHT_PX,
        toolbar: { show: false },
        animations: { enabled: false },
      },
      title: { text: 'PnL Distribution' },
      plotOptions: {
        bar: {
          horizontal: true,
          distributed: true, // colorByPoint: true
          barHeight: '70%',
        },
      },
      legend: { show: false },
      dataLabels: { enabled: false },

      xaxis: {
        // IMPORTANT: for horizontal bars, Apex still wants categories here;
        // it will render them on the left (y-axis visually).
        categories: categories.map(c => truncateLabel(c, 22)),
        min: xMin,
        max: xMax,
        tickAmount: 6,
        title: {
          text: `${repCcy.symbol} value | (Total PnL: ${formattedTotal})`,
        },
        labels: {
          // TS signature in many apex versions: (value: string, timestamp?) => string|string[]
          formatter: (value: string) => {
            const n = Number(value);
            return Number.isFinite(n) ? formatMillions(n) : value;
          },
        },
      },

      // Vertical grid only + visible zero line (like you requested earlier)
      grid: {
        show: true,
        xaxis: { lines: { show: true } },
        yaxis: { lines: { show: false } },
        padding: { left: 12, right: 12 },
      },
      annotations: {
        xaxis: [
          {
            x: 0,
            borderColor: '#555',
            strokeDashArray: 0,
            // label: { show: false },
          },
        ],
      },

      tooltip: {
        y: {
          formatter: (val: number) =>
            `${Math.round(val).toLocaleString('en-US', {
              maximumFractionDigits: 0,
            })} ${repCcy.symbol}`,
          title: { formatter: () => '' },
        },
      },
    };
  }, [categories, minVal, maxVal, repCcy.symbol, formattedTotal]);

  if (!hasData) {
    return (
      <Empty
        description={
          <Text type="secondary">No PnL distribution available yet.</Text>
        }
      />
    );
  }

  return (
    <div style={{ width: '100%' }}>
      <Row>
        <Col span={19}>
          <div style={{ width: 400 }}>
            {activeTab === '100' ? (
              <Chart
                options={options}
                series={series as any}
                type="bar"
                height={CHART_HEIGHT_PX}
              />
            ) : (
              <PerInstrumentPnLDistributionTab
                player={player}
                assetClassNumber={Number(activeTab)}
              />
            )}
          </div>
        </Col>

        <Col span={5}>
          {playableAssetClasses_.map(assetClassNumber => {
            const key = assetClassNumber.toString();
            const isActive = activeTab === key;

            const assetClassName =
              assetClassNumber === 100
                ? 'Overall PnL'
                : ASSET_CLASSES[assetClassNumber];

            return (
              <Button
                type="link"
                key={assetClassNumber}
                onClick={() => setActiveTab(key)}
                className={`pnl-tab-btn ${
                  isActive ? 'pnl-tab-btn--active' : ''
                }`}
              >
                {assetClassName}
              </Button>
            );
          })}
        </Col>
      </Row>
    </div>
  );
};

export default PlayerPnLDistributionTab;
